<template>
    <div class="v-atlas-raumplanung-attribute-selector">

        <!--#region Buttons-->
        <div class="v-atlas-raumplanung-attribute-selector__buttons">

            <SmartButton v-model="showPopup" data-tooltip="Indikator auswählen"
                class="v-atlas-raumplanung-attribute-selector__button" boolean>
                {{ getAttributeLabel() }}
            </SmartButton>

            <button data-tooltip="Informationen zum Indikator anzeigen" @click="showAttributeInfoPanel">
                <span>Info</span>
            </button>


        </div>
        <!--#endregion Buttons-->


        <!--#region Attribute selection popup-->
        <Popup v-model:show="showPopup" title="Indikator auswählen" class="x-w-full">



            <div class="v-atlas-raumplanung-attribute-selector__popup">

                <SplitPanel v-model:ratio="splitPanelRatio">

                    <!--#region Split panel left side-->
                    <template #panel1>
                        <div class="v-atlas-raumplanung-attribute-selector__left">
                            <TocFilterTree :tree="filterTree" :registry="filterRegistry" />
                        </div>
                    </template>
                    <!--#endregion Split panel left side-->


                    <!--#region Split panel right side-->
                    <template #panel2>

                        <div class="v-atlas-raumplanung-attribute-selector__right">

                            <!--#region Right side left half (attributes list)-->
                            <div class="v-atlas-raumplanung-attribute-selector__right-list">
                                <div v-if="isLoading" class="v-atlas-raumplanung-attribute-selector__loading">
                                    Lade Indikatoren …
                                </div>

                                <div v-else-if="error" class="v-atlas-raumplanung-attribute-selector__error">
                                    <p>Die Indikatoren konnten nicht geladen werden.</p>
                                </div>

                                <template v-else>
                                    <div class="v-atlas-raumplanung-attribute-selector__results-panel">

                                        <div v-if="!pinnedChips.length">
                                            <h3>Keine Filter ausgewählt. Es werden alle verfügbaren Indikatoren
                                                angezeigt:</h3>
                                        </div>
                                        <div v-else>
                                            <h3>{{ datasets.length }} Indikatoren entsprechen den gewählten Filtern:
                                            </h3>


                                            <!--
                                            <span v-if="!datasets.length">
                                                Ändern Sie die gewählten Filtereinstellungen.
                                            </span>
                                            -->

                                            <!-- Pinned filters as removable chips -->
                                            <div v-if="pinnedChips.length"
                                                class="v-atlas-raumplanung-attribute-selector__pinned">
                                                <span v-for="chip in pinnedChips" :key="chip.id"
                                                    class="v-atlas-raumplanung-attribute-selector__chip">
                                                    <span class="v-atlas-raumplanung-attribute-selector__chip-label">{{
                                                        chip.label }}</span>
                                                    <button class="v-atlas-raumplanung-attribute-selector__chip-remove"
                                                        :data-tooltip="`Filter lösen: ${chip.label}`"
                                                        @click="unpinChip(chip.id)">
                                                        ×
                                                    </button>
                                                </span>
                                            </div>
                                            <div v-else>

                                            </div>

                                        </div>



                                    </div>

                                    <div class="v-atlas-raumplanung-attribute-selector__table-wrap">
                                        <table class="v-atlas-raumplanung-attribute-selector__table">

                                            <tbody>
                                                <tr v-for="dataset in datasets" :key="dataset.id"
                                                    :class="['v-atlas-raumplanung-attribute-selector__row', { 'v-atlas-raumplanung-attribute-selector__row--selected': dataset.id === modelValue }]"
                                                    @mouseenter="hoveredDataset = dataset"
                                                    @mouseleave="hoveredDataset = null">

                                                    <td class="v-atlas-raumplanung-attribute-selector__cell--label">
                                                        <button
                                                            class="v-atlas-raumplanung-attribute-selector__select-button"
                                                            data-tooltip="Indikator auswählen"
                                                            @click.stop="onRowClick(dataset)">
                                                            {{ dataset.label_toc }}
                                                        </button>
                                                    </td>
                                                    <td class="v-atlas-raumplanung-attribute-selector__cell--favorite">
                                                        <button
                                                            class="v-atlas-raumplanung-attribute-selector__favorite-toggle"
                                                            :class="{ 'v-atlas-raumplanung-attribute-selector__favorite-toggle--active': isFavorite(dataset.id) }"
                                                            :data-tooltip="isFavorite(dataset.id) ? 'Favorit entfernen' : 'Als Favorit markieren'"
                                                            @click.stop="toggleFavorite(dataset.id)">
                                                            {{ isFavorite(dataset.id) ? '★' : '☆' }}
                                                        </button>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>


                                </template>
                            </div>
                            <!--#endregion Right side left half-->


                            <!--#region Right side right half (attribute metadata)-->
                            <div class="v-atlas-raumplanung-attribute-selector__description">
                                <RaumplanungAttributeInfo v-if="hoveredDataset" :metadata="hoveredDataset" />
                                <p v-else class="v-atlas-raumplanung-attribute-selector__description-empty">
                                    Bewegen Sie den Mauszeiger über einen Indikator, um seine Beschreibung zu sehen.

                                </p>
                            </div>
                            <!--#endregion Right side right half-->

                        </div>
                    </template>
                    <!--#endregion Split panel right side-->

                </SplitPanel>
            </div>
        </Popup>
        <!--#endregion Attribute selection popup-->



    </div>
</template>

<script setup lang="ts">




import { computed, ref } from 'vue';
import { Popup, SmartButton, SplitPanel, useSmartFetch, ViewStackController } from 'gertrud';
import { AttributeSelectorRequestOptionsItem } from 'atlas-shared/dist/RequestOptions';
import { RaumplanungAttributeSelectorResponse, RaumplanungAttributeMetadataListEntry, RaumplanungTocFilterTreeNode } from 'atlas-shared';
import { sidebarPanelNames } from '@/sidebarPanelNames';
import { useFavoriteAttributes } from '@/composables/useFavoriteAttributes';
import TocFilterTree from './TocFilterTree.vue';
import { createIndicatorFilterRegistry, IndicatorFilterRegistry } from './filters/filterRegistry';
import RaumplanungAttributeInfo from './RaumplanungAttributeInfo.vue';
import { TextFilter } from './filters/TextFilter';


const props = defineProps<{
    option: AttributeSelectorRequestOptionsItem;
    modelValue: string;
    viewStackController: ViewStackController
}>();


const emit = defineEmits<{
    'update:modelValue': [value: string];
}>();


const showPopup = ref(false);

const splitPanelRatio = ref(30);

/** Per-instance registry so filter/pin state is not shared across selectors. */
const filterRegistry = createIndicatorFilterRegistry();

/** The dataset currently under the mouse cursor, used to show its description. */
const hoveredDataset = ref<RaumplanungAttributeMetadataListEntry | null>(null);

const selectorDataUrl = computed(() => `${props.option.serviceBaseUrl}/attributeSelectorData`);

const { data: selectorDataResponse, isLoading, error } = useSmartFetch<RaumplanungAttributeSelectorResponse>(selectorDataUrl);

const datasets = computed<RaumplanungAttributeMetadataListEntry[]>(() => {
    const list = selectorDataResponse.value?.datasets ?? []
    const registry = filterRegistry
    const filtered = list.filter(d => registry.getEffectivePredicate(d))
    return [...filtered].sort((a, b) => a.label_toc.localeCompare(b.label_toc, "de"))
})

const filterTree = computed<RaumplanungTocFilterTreeNode[]>(() => selectorDataResponse.value?.tree ?? [])

/**
 * The currently pinned filters, rendered as removable chips at the top of the
 * indicator list. Each chip shows the filter's label and an "×" button to unpin it.
 *
 * The chip label is enriched with context:
 * - the text filter shows its current query value,
 * - administrative-level filters are prefixed with "Administrative Ebene: ",
 * - Thema (category tag) filters are prefixed with "Thema: ".
 */
const pinnedChips = computed<Array<{ id: string; label: string }>>(() =>
    filterRegistry.getPinnedIds()
        .map(id => filterRegistry.getFilter(id))
        .filter((f): f is NonNullable<typeof f> => f !== null)
        .map(f => ({ id: f.id, label: chipLabelFor(f) })),
);

/** Builds the enriched chip label for a pinned filter. */
function chipLabelFor(f: NonNullable<ReturnType<IndicatorFilterRegistry['getFilter']>>): string {
    if (f.id === 'text') {
        const query = (f as TextFilter).query.value.trim()
        return query ? `Textsuche: ${query}` : f.label
    }
    if (f.id.startsWith('regionalDepth:')) {
        return `Administrative Ebene: ${f.label}`
    }
    if (f.id.startsWith('tag:')) {
        return `Thema: ${f.label}`
    }
    return f.label
}

/** Unpins a filter by its id (used by the pinned-filter chips). */
function unpinChip(id: string): void {
    filterRegistry.unpin(id)
}


function getAttributeLabel(): string {
    const dataset = datasets.value.find(d => d.id === props.modelValue)
    return dataset?.label || "Kein Indikator ausgewählt"
}

function showAttributeInfoPanel() {
    props.viewStackController.addItem({
        componentName: sidebarPanelNames.RAUMPLANUNG_ATTRIBUTE_INFO,
        props: {
            metadata: datasets.value.find(d => d.id === props.modelValue) ?? null,
        },
        role: 'raumplanung-attribute-info',
        id: '',
        title: `Über den Indikator '${getAttributeLabel()}'`
    }

    )
}

function onRowClick(dataset: RaumplanungAttributeMetadataListEntry) {
    emit("update:modelValue", dataset.id)
    showPopup.value = false
}


//#region Favorites

const { isFavorite, toggleFavorite } = useFavoriteAttributes()

//#endregion
</script>